/**
 * What the nursery offers this customer, indexed once (V5, docs/15).
 *
 * `portal_availability` answers with every plant in stock - a thousand of them
 * in a busy season - and the page searches and filters that on each keystroke.
 * So the answer is indexed here once, when it arrives: each plant by product,
 * its price resolved, and the words a search looks in. After that a keystroke
 * is one pass over short strings, and a row on screen is a lookup rather than
 * a trawl through the price rows.
 *
 * **No second copy of the pricing rules.** The price rows are this customer's
 * - their tier's and their own overrides - for the plants in stock, and the
 * price is chosen by `resolvePrice` from `@smf/core`: the function the desktop
 * runs. A plant with no price reads "price on confirmation" and can still be
 * ordered. The price a line shows is what the customer saw, not what they will
 * pay: the nursery prices the order when it confirms it.
 */

import { type Cents, type PriceCandidate, cents, resolvePrice } from '@smf/core';

import type { Availability, Category, ListedItem, PriceRow } from './portal-api.js';

function candidate(row: PriceRow): PriceCandidate {
  return {
    id: row.id,
    priceCents: cents(row.priceCents),
    effectiveFrom: row.effectiveFrom,
    ...(row.effectiveTo === undefined ? {} : { effectiveTo: row.effectiveTo }),
  };
}

/** The rows grouped by product, in one pass. */
function byProduct(rows: readonly PriceRow[]): Map<string, PriceCandidate[]> {
  const grouped = new Map<string, PriceCandidate[]>();
  for (const row of rows) {
    const group = grouped.get(row.productId);
    if (group === undefined) grouped.set(row.productId, [candidate(row)]);
    else group.push(candidate(row));
  }
  return grouped;
}

function resolve(
  tier: readonly PriceCandidate[],
  overrides: readonly PriceCandidate[],
  hasTier: boolean,
  asAt: string,
): Cents | undefined {
  const resolution = resolvePrice({ tierPrices: tier, overrides, asAt, hasTier });
  return resolution.found ? resolution.priceCents : undefined;
}

/** This customer's unit price for a plant on a day, or absent: "price on confirmation". */
export function priceOf(
  prices: Availability['prices'],
  productId: string,
  asAt: string,
): Cents | undefined {
  return resolve(
    byProduct(prices.tier).get(productId) ?? [],
    byProduct(prices.overrides).get(productId) ?? [],
    prices.hasTier,
    asAt,
  );
}

/** Lower case, accents folded, so "hebe" finds "Hébé" and the other way about. */
function folded(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export interface Catalogue {
  /** In the server's order: by category, then description. */
  readonly items: readonly ListedItem[];
  readonly categories: readonly Category[];
  /** The plants in stock, by product. One missing is no longer in stock. */
  readonly listed: ReadonlyMap<string, ListedItem>;
  /** This customer's unit price today, for the plants that have one. */
  readonly prices: ReadonlyMap<string, Cents>;
  readonly taxInclusive: boolean;
  /** Per product, the name, variety and code a search looks in, folded. */
  readonly words: ReadonlyMap<string, string>;
}

export function catalogueOf(availability: Availability, asAt: string): Catalogue {
  const tier = byProduct(availability.prices.tier);
  const overrides = byProduct(availability.prices.overrides);
  const listed = new Map<string, ListedItem>();
  const prices = new Map<string, Cents>();
  const words = new Map<string, string>();

  for (const item of availability.items) {
    listed.set(item.productId, item);
    const price = resolve(
      tier.get(item.productId) ?? [],
      overrides.get(item.productId) ?? [],
      availability.prices.hasTier,
      asAt,
    );
    if (price !== undefined) prices.set(item.productId, price);
    words.set(
      item.productId,
      folded([item.description, item.variety, item.sku].join(' ')),
    );
  }

  return {
    items: availability.items,
    categories: availability.categories,
    listed,
    prices,
    taxInclusive: availability.prices.taxInclusive,
    words,
  };
}

/**
 * The plants a search and a category leave, in the catalogue's order. Every
 * word typed must appear somewhere in the name, variety or code, so "lav 14"
 * finds "Lavandula Hidcote 14cm". An empty category is every category.
 */
export function matching(
  catalogue: Catalogue,
  query: string,
  categoryId: string,
): readonly ListedItem[] {
  const terms = folded(query)
    .split(/\s+/)
    .filter((term) => term !== '');
  if (terms.length === 0 && categoryId === '') return catalogue.items;
  return catalogue.items.filter((item) => {
    if (categoryId !== '' && item.categoryId !== categoryId) return false;
    const words = catalogue.words.get(item.productId) ?? '';
    return terms.every((term) => words.includes(term));
  });
}
